/**
 * Pure scroll-geometry helpers for the scene-board views.
 * No imports, no side effects — all logic is expressed as pure functions
 * that accept measured geometry so they can be unit-tested without jsdom layout.
 *
 * Slice 2 will append more exports (sceneAtScrollLeft, scrollLeftForScene).
 * Keep the module open for extension.
 */

/** Content-space pixel extents of one scene in a vertical scroll container.
 *  start = top edge of the scene element relative to scroll content origin.
 *  end   = bottom edge of the scene element relative to scroll content origin.
 */
export interface SceneEdge {
  sceneId: number;
  start: number;
  end: number;
}

/**
 * Returns the sceneId of the FIRST edge (array order) whose `end` is strictly
 * greater than `viewportStart` — i.e. the topmost scene that has at least one
 * pixel visible at or below the viewport's top edge.
 * Returns null when the list is empty or all scenes are above viewportStart.
 */
export function firstVisibleSceneId(
  edges: SceneEdge[],
  viewportStart: number,
): number | null {
  for (const edge of edges) {
    if (edge.end > viewportStart) return edge.sceneId;
  }
  return null;
}

/**
 * Returns the scrollTop that brings [targetStart, targetEnd] into the viewport
 * using minimal movement:
 *   - already fully visible  → currentOffset (no move)
 *   - targetStart < currentOffset (above) → targetStart (align leading edge)
 *   - else (trailing below)  → targetEnd - viewportSize (align trailing edge)
 * Result is always clamped to >= 0.
 */
export function minimalScrollOffsetToReveal(args: {
  targetStart: number;
  targetEnd: number;
  viewportSize: number;
  currentOffset: number;
}): number {
  const { targetStart, targetEnd, viewportSize, currentOffset } = args;
  const viewportEnd = currentOffset + viewportSize;
  // Fully visible: both edges within [currentOffset, viewportEnd]
  if (targetStart >= currentOffset && targetEnd <= viewportEnd) {
    return currentOffset;
  }
  let result: number;
  if (targetStart < currentOffset) {
    // Target is above viewport: align leading edge
    result = targetStart;
  } else {
    // Target bottom hangs below viewport: align trailing edge
    result = targetEnd - viewportSize;
  }
  return Math.max(0, result);
}

/**
 * Encodes the mount-time scroll decision for a board view.
 *   restore   — apply the exact saved offset (anchor unchanged since last visit)
 *   reanchor  — scroll just enough to show the named scene (anchor changed)
 *   none      — no anchor; leave at top / default
 */
export type ScrollDecision =
  | { kind: 'restore'; offset: number }
  | { kind: 'reanchor'; sceneId: number }
  | { kind: 'none' };

/**
 * Decides what the mounting view should do with its scroll position:
 *   currentAnchor == null                          → none
 *   savedOffset != null && savedAnchor === currentAnchor → restore savedOffset
 *   otherwise                                      → reanchor to currentAnchor
 */
export function pickScrollTarget(args: {
  savedAnchor: number | null;
  savedOffset: number | null;
  currentAnchor: number | null;
}): ScrollDecision {
  const { savedAnchor, savedOffset, currentAnchor } = args;
  if (currentAnchor === null) return { kind: 'none' };
  if (savedOffset !== null && savedAnchor === currentAnchor) {
    return { kind: 'restore', offset: savedOffset };
  }
  return { kind: 'reanchor', sceneId: currentAnchor };
}

// ─────────────────────────────────────────────────────────────────────────────
// Slice 2: Timeline ⇄ Board time-mapping helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A scene's position in absolute timeline time (seconds).
 * `start` and `end` use the same coordinate space as `getViewOffset()`:
 * 0 in all-scenes mode (activeSceneId===null), or the scene's cumulative
 * offset from the beginning of the full project timeline in single-scene mode.
 */
export interface SceneTimeSpan {
  sceneId: number;
  /** Absolute timeline start time of this scene (seconds, inclusive). */
  start: number;
  /** Absolute timeline end time of this scene (seconds, exclusive). */
  end: number;
}

/**
 * Returns the sceneId whose span satisfies `span.start <= t < span.end`.
 * Edge cases:
 *   - `t < spans[0].start`           → returns first scene id
 *   - `t >= spans[last].end`          → returns last scene id
 *   - empty spans array               → returns null
 */
export function sceneAtTime(spans: SceneTimeSpan[], timeSeconds: number): number | null {
  if (spans.length === 0) return null;
  if (timeSeconds < spans[0].start) return spans[0].sceneId;
  for (const s of spans) {
    if (timeSeconds >= s.start && timeSeconds < s.end) return s.sceneId;
  }
  return spans[spans.length - 1].sceneId;
}

/**
 * Converts an absolute timeline time (seconds) to a ShotLane `scrollLeft`
 * pixel value.  Mirrors ShotLane's X formula: `(t - viewOffset) * pixelsPerSecond`.
 * Clamped to 0 — `scrollLeft` is never negative.
 */
export function timeToScrollLeft(
  timeSeconds: number,
  pixelsPerSecond: number,
  viewOffset: number,
): number {
  return Math.max(0, (timeSeconds - viewOffset) * pixelsPerSecond);
}

/**
 * Inverse of `timeToScrollLeft`.  Returns `viewOffset` when `pixelsPerSecond`
 * is 0 to guard against divide-by-zero.
 */
export function scrollLeftToTime(
  scrollLeft: number,
  pixelsPerSecond: number,
  viewOffset: number,
): number {
  return pixelsPerSecond > 0 ? scrollLeft / pixelsPerSecond + viewOffset : viewOffset;
}

// ─────────────────────────────────────────────────────────────────────────────
// Expand/collapse scroll anchoring
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Keeps a toggled scene visually framed when an expand/collapse changes scene
 * heights. Exclusive expansion can collapse a scene ABOVE the clicked one,
 * removing its height so the clicked scene jumps upward (the user lands
 * mid-scene). Given the clicked scene's viewport-relative top before the layout
 * change (`prevTop`) and after (`newTop`), returns the `scrollTop` that restores
 * it to `prevTop`: `currentScrollTop + (newTop - prevTop)`. Clamped to >= 0
 * (the browser clamps the upper bound to max scroll on assignment).
 *
 * Needed because the #1 embedded target (WKWebView) does not support CSS
 * `overflow-anchor`, so native scroll anchoring cannot compensate.
 */
export function scrollTopToKeepAnchored(args: {
  currentScrollTop: number;
  prevTop: number;
  newTop: number;
}): number {
  const { currentScrollTop, prevTop, newTop } = args;
  return Math.max(0, currentScrollTop + (newTop - prevTop));
}
